import React, { useEffect, useState } from 'react';
import { CollapseBlock, QuestionModal, Spinner } from '../../../../common';
import PollsTable from './PollsTable.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient';
import { AppBarCMS } from '../../../../basic';
import {
  BarChartOutlined,
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  DeleteFilled,
  EditOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useActivateDeactivatePollMutation,
  useDeletePollMutation,
  useGetPollsQuery,
} from '../../../../../redux/services/polls';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { IconButton } from '../../../../common/controls/Controls.jsx';
import useUserHasRole from '../../../../../hooks/useUserHasRoles.jsx';
import { ROLE_POOLS_ADMIN } from '../../../../../utils/constants/roles.js';

const DELETE_CONFIRM_CLOSE = -1;
const PollsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useGetPollsQuery();
  const isPollsAdmin = useUserHasRole(ROLE_POOLS_ADMIN);
  // polls with recorded responses can be edited only by polls administrators
  const canEditPoll = (row) => row.noOfResponses === 0 || isPollsAdmin;
  const [deletePoll, { isSuccess: isDeleteSuccess }] = useDeletePollMutation();

  const [changeStatus] = useActivateDeactivatePollMutation();

  const {
    deleteSuccess: showDeleteSuccess,
    saveSuccess: showSaveSuccess,
    successMessage: successMessage,
    errorMessage: errorMessage,
  } = useToastMessages();

  useEffect(() => {
    document.title = t('Ankety') + ' - FRI CMS';
  }, []); // Runs on mount

  const [confirmDeleteIndex, setDeleteIndex] = useState(DELETE_CONFIRM_CLOSE);

  useEffect(() => {
    if (isDeleteSuccess) {
      showDeleteSuccess();
      refetch();
    }
  }, [isDeleteSuccess]);

  const activateDeactivatePoll = async (id, active) => {
    try {
      await changeStatus({ id: id, active: active === 0 }).unwrap();
      showSaveSuccess();
      refetch();
    } catch (err) {
      //showSaveFailed(err);
    }
  };

  const customSettings = (row) => {
    return (
      <div style={{ display: 'flex' }}>
        <IconButton
          icon={
            <EditOutlined style={{ color: canEditPoll(row) ? '' : 'gray' }} />
          }
          tooltip={t('Upraviť')}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`${routes.cmsPollEdit}/${row.id}`);
          }}
          disabled={!canEditPoll(row)}
          // rovnaký text je použitý aj v PollForm.jsx
          disabledTooltip={t(
            'Nie je možné upraviť anketu, ktorá má zaznamenané odpovede.',
          )}
        />
        <IconButton
          icon={
            <CopyOutlined
              style={{
                color: row.active === 0 && row.show_results === 0 ? 'gray' : '',
              }}
            />
          }
          tooltip={t('Skopírovať odkaz')}
          onClick={(e) => {
            e.stopPropagation();
            // Define the full URL to copy
            const fullUrl = `${window.location.protocol}//${window.location.hostname}/anketa/${row.slug}`;

            // Copy to clipboard
            navigator.clipboard
              .writeText(fullUrl)
              .then(() => {
                successMessage({
                  message: t('URL adresa bola skopírovaná do schránky.'),
                });
              })
              .catch(() => {
                errorMessage({
                  message: t('Skopírovanie URL adresy do schránky zlyhalo.'),
                });
              });
          }}
          disabled={row.active === 0 && row.show_results === 0}
          disabledTooltip={t('Anketa nie je aktívna')}
        />
        <IconButton
          icon={<BarChartOutlined />}
          tooltip={t('Zobraziť výsledky')}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`${routes.cmsPollResults}/${row.id}`);
          }}
        />
        <IconButton
          icon={row.active === 0 ? <CheckOutlined /> : <CloseOutlined />}
          tooltip={row.active === 0 ? t('Aktivovať') : t('Deaktivovať')}
          onClick={(e) => {
            e.stopPropagation();
            activateDeactivatePoll(row.id, row.active);
          }}
        />
        <IconButton
          icon={<DeleteFilled />}
          tooltip={t('Vymazať')}
          onClick={(e) => {
            e.stopPropagation();
            setDeleteIndex(row.id);
          }}
        />
      </div>
    );
  };

  return isLoading ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS
        title={t('Anketa')}
        backUrl={routes.cmsDashboard}
        backUrlTooltip={t('Nástenka')}
        createText={t('Pridať anketu')}
        createUrl={routes.cmsPollCreate}
        permissionsBasic={'polls'}
        mainActions={[
          {
            onClick: () => navigate(routes.cmsResponseCollections),
            title: t('Kolekcie odpovedí'),
            icon: <UnorderedListOutlined />,
          },
        ]}
      />
      <div style={{ margin: '10px' }}>
        <CollapseBlock>
          <PollsTable
            polls={data}
            onCreateClick={() => navigate(`${routes.cmsPollCreate}`)}
            onEdit={(row) => {
              if (canEditPoll(row)) navigate(`${routes.cmsPollEdit}/${row.id}`);
            }}
            customSettings={customSettings}
            optionsWidth={238}
          />
        </CollapseBlock>
      </div>
      <QuestionModal
        chosenPositive={() => deletePoll(confirmDeleteIndex)}
        question={t('Naozaj chcete vymazať túto položku?')}
        visible={confirmDeleteIndex !== DELETE_CONFIRM_CLOSE}
        yes={t('Áno')}
        toggle={() => setDeleteIndex(DELETE_CONFIRM_CLOSE)}
        headerTitle={t('Vymazať položku')}
      />
    </>
  );
};

export default PollsPage;
